import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { WheatBorder } from '../ui/WheatDecoration';
import { useSignup } from '../../context/SignupContext';
import { SignupStep } from '../../types';
import { validateOTP } from '../../utils/validation';
import { verifyOTP, resendOTP } from '../../services/api';

// Helper function to mask phone number, showing only last 4 digits
const maskPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');
  // If less than 4 digits, return as is
  if (digits.length <= 4) return digits;
  // Replace all but last 4 digits with asterisks
  const masked = `(***) ***-${digits.slice(-4)}`;
  // Format with dashes if original had them
  return phoneNumber.includes('-') 
    ? masked.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
    : masked;
};

export const OTPVerification: React.FC = () => {
  const { 
    signupData, 
    setCurrentStep, 
    isLoading, 
    setIsLoading, 
    setError
  } = useSignup();

  const [otp, setOtp] = useState('');
  const [validationError, setValidationError] = useState<string>('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);
  
  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Set focus on OTP input when component mounts
  useEffect(() => {
    if (otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, []);

  // Redirect if no signup data
  if (!signupData) {
    setCurrentStep(SignupStep.SIGNUP);
    return null;
  }

  const handleOtpChange = (value: string) => {
    // Only allow digits, limit to 6 characters
    const cleanValue = value.replace(/\D/g, '').slice(0, 6);
    setOtp(cleanValue);
    
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }
    
    // Auto-submit when exactly 6 digits are entered
    if (cleanValue.length === 6) {
      setTimeout(() => handleSubmit(null, cleanValue), 100);
    }
  };

  const handleSubmit = async (e: React.FormEvent | null, otpValue?: string) => {
    if (e) e.preventDefault();
    
    const otpToVerify = otpValue || otp;
    
    // Validate OTP
    const errors = validateOTP(otpToVerify);
    if (errors.otp) {
      setValidationError(errors.otp);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      await verifyOTP({
        phoneNumber: signupData.phoneNumber,
        otp: otpToVerify
      });
      
      // Move to success step
      setCurrentStep(SignupStep.SUCCESS);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || isResending) return;
    
    setIsResending(true);
    setError(null);
    
    try {
      await resendOTP(signupData.phoneNumber);
      setResendCooldown(60); // 60 second cooldown
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(SignupStep.SIGNUP);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="container">
        <Card className="relative overflow-hidden">
          <WheatBorder />
          <div className="relative z-10">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-bakery-brown font-script mb-2">
                Verify Your Phone
              </h1>
              <p className="text-bakery-brown/80 text-lg mb-4">
                Enter the 6-digit code sent to
              </p>
              <p className="text-bakery-brown font-semibold">
                {maskPhoneNumber(signupData.phoneNumber)}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex flex-col items-center">
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => handleOtpChange(e.target.value)}
                  placeholder="000000"
                  maxLength={6}
                  className="otp-input"
                  ref={otpInputRef}
                  disabled={isLoading}
                  autoComplete="one-time-code"
                />
                {validationError && (
                  <p className="input-error mt-2 text-center">{validationError}</p>
                )}
              </div>

              <div className="space-y-4">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading || otp.length !== 6}
                  isLoading={isLoading}
                >
                  Verify Code
                </Button>

                <div className="text-center">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleResend}
                    disabled={resendCooldown > 0 || isResending}
                    className="text-bakery-brown hover:text-bakery-dark-brown"
                  >
                    {isResending ? 'Sending...' : 
                     resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 
                     'Resend Code'}
                  </Button>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  disabled={isLoading}
                  className="w-full"
                >
                  Back
                </Button>
              </div>
            </form>
          </div>
        </Card>
      </div>
    </div>
  );
};
